import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useObjetivosSancor } from '../../pages/ReporteAvanzados/objetivosSancor/useObjetivosSancor';

// --- COMPONENTE SKELETON LOADER CON ANIMACIÓN PULSANTE ---
const WidgetSkeleton = () => (
    <div className="space-y-3 animate-pulse text-left">
        {/* Skeleton de la sección principal (Automotores) */}
        <div className="bg-slate-100 p-3.5 rounded-xl space-y-2.5 border border-slate-200/60">
            <div className="flex justify-between items-center">
                <div className="h-3 bg-slate-200 rounded-md w-36" />
                <div className="h-4 bg-slate-200 rounded-md w-10" />
            </div>
            <div className="flex justify-between items-baseline">
                <div className="h-6 bg-slate-200 rounded-md w-28" />
                <div className="h-3 bg-slate-200 rounded-md w-16" />
            </div>
            <div className="flex gap-1.5 pt-1">
                <div className="h-4 bg-slate-200 rounded w-14" />
                <div className="h-4 bg-slate-200 rounded w-14" />
                <div className="h-4 bg-slate-200 rounded w-16" />
            </div>
            <div className="h-2 bg-slate-200 rounded-full w-full mt-1" />
        </div>

        {/* Skeleton del grid secundario (Hogar + AP) */}
        <div className="grid grid-cols-2 gap-2">
            <div className="p-3 bg-slate-100 rounded-xl h-14 border border-slate-200/60" />
            <div className="p-3 bg-slate-100 rounded-xl h-14 border border-slate-200/60" />
        </div>

        {/* Skeleton de Cross-Selling */}
        <div className="p-3 bg-slate-100 rounded-xl h-12 border border-slate-200/60" />
    </div>
);

const WidgetObjetivosSancor = () => {
    const navigate = useNavigate();
    const {
        selectedCuatrimestreId,
        setSelectedCuatrimestreId,
        cuatrimestresList,
        loading,
        metrics
    } = useObjetivosSancor();

    const isAgrupado = metrics?.isAgrupadoAutoMoto ?? true;

    const autosMotosGlobal = metrics?.riesgos?.autosMotos || { 
        neto: 0, 
        metaCuatri: 150, 
        porcCuatri: 0, 
        altas: 0, 
        bajas: 0, 
        autosSoloNeto: 0, 
        motosSoloNeto: 0, 
        camionesSoloNeto: 0 
    };

    const autosCamion = metrics?.riesgos?.autosCamion || { neto: 0, metaCuatri: 70, porcCuatri: 0, altas: 0, bajas: 0 };
    const motos = metrics?.riesgos?.motos || { neto: 0, metaCuatri: 30, porcCuatri: 0, altas: 0, bajas: 0 };

    const hogar = metrics?.riesgos?.hogar || { neto: 0, metaCuatri: 88 };
    const ap = metrics?.riesgos?.ap || { neto: 0, metaCuatri: 200 };
    const crossSelling = metrics?.crossSelling || { ratio: 0, totalCross: 0, totalHogarAltas: 0 };

    // CONDICIÓN DE CARGA INICIAL PERFECTA: Si está consultando o aún no se procesaron los datos
    const isInitialLoading = loading || !metrics?.riesgos;

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all p-5 flex flex-col justify-between text-left space-y-4 relative overflow-hidden">
            
            {/* CABECERA WIDGET */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2.5">
                    <div className="p-2 bg-amber-50 rounded-xl border border-amber-100 shrink-0">
                        <span className="text-base">🏆</span>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="font-black text-slate-800 text-sm tracking-tight">
                                Desafío SANCOR
                            </h3>
                            {loading && (
                                <span className="flex h-2 w-2 relative">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                                </span>
                            )}
                        </div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                            Meta Trimestral
                        </p>
                    </div>
                </div>

                {/* SELECTOR TRIMESTRAL (Q1, Q2, Q3, Q4) */}
                <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 shrink-0 flex-wrap gap-0.5">
                    {cuatrimestresList.map(q => (
                        <button
                            key={q.id}
                            onClick={() => setSelectedCuatrimestreId(q.id)}
                            className={`px-2 py-0.5 rounded-lg text-[10px] font-black transition-all cursor-pointer ${
                                q.id === selectedCuatrimestreId 
                                    ? 'bg-white text-blue-900 shadow-xs' 
                                    : 'text-slate-400 hover:text-slate-700'
                            }`}
                        >
                            {q.id}
                        </button>
                    ))}
                </div>
            </div>

            {/* RENDERIZADO CONDICIONAL: SKELETON O CONTENIDO FINAL */}
            {isInitialLoading ? (
                <WidgetSkeleton />
            ) : (
                <div className="space-y-3 animate-fade-in">
                    {/* SECCIÓN PRINCIPAL DE AUTOMOTORES */}
                    {isAgrupado ? (
                        /* VISTA GENERAL: AUTOS, MOTOS & CAMIONES AGRUPADOS (META 150) */
                        <div className="bg-slate-50/70 p-3.5 rounded-xl border border-slate-150 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                                    <span>🚗🏍️🚛</span> Autos, Motos & Camiones
                                </span>
                                <span className="text-xs font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100 font-mono">
                                    {autosMotosGlobal.porcCuatri}%
                                </span>
                            </div>

                            <div className="flex items-baseline justify-between">
                                <div className="text-xl font-black text-slate-900 font-mono">
                                    {autosMotosGlobal.neto} <span className="text-xs text-slate-400 font-normal">/ {autosMotosGlobal.metaCuatri} netos</span>
                                </div>
                                <div className="text-[10px] font-mono font-bold">
                                    <span className="text-emerald-600">+{autosMotosGlobal.altas}</span>
                                    <span className="text-slate-300 mx-0.5">/</span>
                                    <span className="text-rose-500">-{autosMotosGlobal.bajas}</span>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-1.5 text-[9px] font-mono font-bold pt-0.5">
                                <span className="text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">
                                    Autos: {autosMotosGlobal.autosSoloNeto || 0}
                                </span>
                                <span className="text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100">
                                    Motos: {autosMotosGlobal.motosSoloNeto || 0}
                                </span>
                                <span className="text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                                    Camiones: {autosMotosGlobal.camionesSoloNeto || 0}
                                </span>
                            </div>

                            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden mt-1">
                                <div 
                                    className={`h-full transition-all duration-500 ${
                                        autosMotosGlobal.porcCuatri >= 100 ? 'bg-emerald-500' : 'bg-indigo-600'
                                    }`}
                                    style={{ width: `${Math.min(autosMotosGlobal.porcCuatri, 100)}%` }}
                                />
                            </div>
                        </div>
                    ) : (
                        /* VISTA ESPECÍFICA (MARCOS / MAXI / REBECA): AUTOS & CAMIONES Y MOTOS SEPARADOS */
                        <div className="grid grid-cols-2 gap-2">
                            <div className="p-3 bg-indigo-50/50 border border-indigo-100 rounded-xl space-y-1">
                                <span className="text-[9px] font-black text-indigo-700 uppercase block tracking-tight truncate">
                                    🚗 Autos & Camiones
                                </span>
                                <span className="text-sm font-black text-indigo-900 font-mono block">
                                    {autosCamion.neto} <span className="text-[9px] font-normal text-slate-400">/{autosCamion.metaCuatri}</span>
                                </span>
                            </div>

                            <div className="p-3 bg-sky-50/50 border border-sky-100 rounded-xl space-y-1">
                                <span className="text-[9px] font-black text-sky-700 uppercase block tracking-tight truncate">
                                    🏍️ Motos
                                </span>
                                <span className="text-sm font-black text-sky-900 font-mono block">
                                    {motos.neto} <span className="text-[9px] font-normal text-slate-400">/{motos.metaCuatri}</span>
                                </span>
                            </div>
                        </div>
                    )}

                    {/* GRID SECUNDARIO: COMBINADO FAMILIAR / HOGAR + AP */}
                    <div className="grid grid-cols-2 gap-2 text-center">
                        <div className="p-2.5 bg-emerald-50/50 border border-emerald-100 rounded-xl flex flex-col justify-between">
                            <span className="text-[9px] font-black text-emerald-700 uppercase block tracking-tight">
                                🏡 Combinado Familiar
                            </span>
                            <span className="text-sm font-black text-emerald-900 font-mono block mt-1">
                                {hogar.neto} <span className="text-[9px] font-normal text-slate-400">/{hogar.metaCuatri} netos</span>
                            </span>
                        </div>

                        <div className="p-2.5 bg-amber-50/50 border border-amber-100 rounded-xl flex flex-col justify-between">
                            <span className="text-[9px] font-black text-amber-700 uppercase block tracking-tight">
                                🩹 AP
                            </span>
                            <span className="text-sm font-black text-amber-900 font-mono block mt-1">
                                {ap.neto} <span className="text-[9px] font-normal text-slate-400">/{ap.metaCuatri} netos</span>
                            </span>
                        </div>
                    </div>

                    {/* TARJETA DE CROSS-SELLING HOGAR */}
                    <div className="p-3 bg-purple-50/60 border border-purple-100 rounded-xl flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="text-sm">🔀</span>
                            <div>
                                <span className="text-[10px] font-black text-purple-900 uppercase block tracking-tight leading-none">
                                    Cross-Selling Hogar
                                </span>
                                <span className="text-[9px] text-purple-600 font-medium block mt-0.5">
                                    Venta Cruzada Pack
                                </span>
                            </div>
                        </div>
                        <div className="text-right font-mono">
                            <span className="text-sm font-black text-purple-800 block leading-none">
                                {crossSelling.ratio}%
                            </span>
                            <span className="text-[9px] font-bold text-slate-400 block mt-0.5">
                                {crossSelling.totalCross} / {crossSelling.totalHogarAltas} packs
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* BOTÓN IR AL REPORTE COMPLETO */}
            <button
                onClick={() => navigate('/ReportesHub?modulo=OBJETIVOS_SANCOR')}
                className="w-full py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-xs flex items-center justify-center gap-2 group cursor-pointer"
            >
                <span>Ver Tablero Completo</span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
            </button>

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default WidgetObjetivosSancor;